import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Heading,
  IconButton,
  Image,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FaArrowLeft, FaShareAlt } from 'react-icons/fa';

const FALLBACK_GRADIENT = 'linear(to-r, #ffcc00, #3eb297, #36a9e1, #e95995)';

/**
 * @param {{
 *   title: string;
 *   excerpt?: string; usato solo per il testo di condivisione, non mostrato nell’hero.
 *   dateLabel: string;
 *   heroImage?: { url?: string; alt?: string } | null;
 *   heroHeights: { base: string; md: string };
 *   shareUrl?: string | null;
 * }} props
 */
const LogbookHero = ({
  title,
  excerpt,
  dateLabel,
  heroImage,
  heroHeights,
  shareUrl,
}) => {
  const hasImg = Boolean(heroImage?.url);
  const toast = useToast();

  const handleShare = useCallback(async () => {
    const url = (shareUrl || '').trim();
    if (!url) return;
    const shareText = excerpt?.trim() || title;
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title, text: shareText, url });
        return;
      }
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast({
          title: 'Link copiato',
          description: "Incollalo dove vuoi condividere l'articolo.",
          status: 'success',
          duration: 2500,
          isClosable: true,
        });
        return;
      }
      window.prompt('Copia il link:', url);
    } catch (err) {
      if (err && typeof err === 'object' && err.name === 'AbortError') return;
      try {
        if (
          typeof navigator !== 'undefined' &&
          navigator.clipboard?.writeText
        ) {
          await navigator.clipboard.writeText(url);
          toast({
            title: 'Link copiato',
            status: 'success',
            duration: 2500,
            isClosable: true,
          });
        }
      } catch {
        toast({
          title: 'Condivisione non disponibile',
          description: "Copia manualmente l'indirizzo dalla barra del browser.",
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
      }
    }
  }, [shareUrl, title, excerpt, toast]);

  return (
    <Box
      position="relative"
      w="100%"
      minH={{ base: heroHeights.base, md: heroHeights.md }}
    >
      {hasImg ? (
        <Image
          src={heroImage.url}
          alt={heroImage.alt || title}
          w="100%"
          h={{ base: heroHeights.base, md: heroHeights.md }}
          objectFit="cover"
          display="block"
        />
      ) : (
        <Box
          w="100%"
          h={{ base: heroHeights.base, md: heroHeights.md }}
          bgGradient={FALLBACK_GRADIENT}
        />
      )}
      <Box
        position="absolute"
        inset={0}
        bgGradient="linear(to-t, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.35) 45%, transparent 100%)"
        pointerEvents="none"
      />
      <IconButton
        as={Link}
        to="/logbook"
        aria-label="Torna al diario di bordo"
        icon={<FaArrowLeft />}
        position="absolute"
        top={{ base: 3, md: 4 }}
        left={{ base: 3, md: 4 }}
        zIndex={2}
        size="md"
        rounded="full"
        color="white"
        bg="blackAlpha.500"
        backdropFilter="blur(8px)"
        _hover={{ bg: 'blackAlpha.700' }}
        _active={{ bg: 'blackAlpha.800' }}
        boxShadow="0 2px 12px rgba(0,0,0,0.35)"
      />
      {shareUrl ? (
        <IconButton
          aria-label="Condividi articolo"
          icon={<FaShareAlt />}
          position="absolute"
          top={{ base: 3, md: 4 }}
          right={{ base: 3, md: 4 }}
          zIndex={2}
          size="md"
          rounded="full"
          color="white"
          bg="blackAlpha.500"
          backdropFilter="blur(8px)"
          _hover={{ bg: 'blackAlpha.700' }}
          _active={{ bg: 'blackAlpha.800' }}
          boxShadow="0 2px 12px rgba(0,0,0,0.35)"
          onClick={handleShare}
        />
      ) : null}
      <Box
        position="absolute"
        left={0}
        right={0}
        bottom={0}
        zIndex={1}
        px={{ base: 5, md: 8 }}
        pb={{ base: 8, md: 10 }}
        pt={16}
      >
        {dateLabel ? (
          <Text
            fontSize="xs"
            color="whiteAlpha.900"
            textTransform="uppercase"
            letterSpacing="wider"
            mb={2}
          >
            {dateLabel}
          </Text>
        ) : null}
        <Heading
          as="h1"
          color="white"
          fontSize={{ base: '2xl', md: '4xl' }}
          lineHeight="short"
          textShadow="0 2px 12px rgba(0,0,0,0.45)"
        >
          {title}
        </Heading>
      </Box>
    </Box>
  );
};

export default LogbookHero;
